<template>
  <div class="vue-pincode-input-wrapper">
    <div  v-for="(cell, index) in cells"
          :key="cell.key">
      <input
        :ref="`${baseRefName}${index}`"
        v-model.trim="cell.value"
        v-bind="$attrs"
        class="vue-pincode-input"
        type="number"
        @focus="focusedCellIdx = index"
        @keydown.delete="onCellErase(index, $event)"
        @keydown="onKeyDown"
      >
      <span class="mx-2" v-if="index === 2"></span>
    </div>

  </div>
</template>

<script>
import Vue from 'vue'

const BASE_REF_NAME = 'vue-pincode-input'
const CELL_REGEXP = '^\\d{1}$'
const DEFAULT_INPUT_TYPE = 'tel'
const SECURE_INPUT_TYPE = 'password'

export default Vue.extend({
  props: {
    value: {
      type: String,
      required: true
    },
    length: {
      type: Number,
      default: 4
    },
    autofocus: {
      type: Boolean,
      default: true
    },
    secure: {
      type: Boolean,
      default: false
    },
    characterPreview: {
      type: Boolean,
      default: true
    },
    charPreviewDuration: {
      type: Number,
      default: 300
    }
  },

  data: () => ({
    baseRefName: BASE_REF_NAME,
    focusedCellIdx: 0,
    cells: [],
    watchers: {},
    cellsInputTypes: {}
  }),

  computed: {
    pinCodeComputed () {
      return this.cells.reduce((pin, cell) => pin + cell.value, '')
    }
  },

  watch: {
    value (value) {
      if (value) {
        this.onParentValueUpdated()
      } else {
        this.reset()
      }
    },

    length () {
      this.reset()
    },

    pinCodeComputed (val) {
      this.$emit('input', val)
    }
  },

  mounted () {
    this.init()
    this.onParentValueUpdated()

    if (this.autofocus) {
      this.$nextTick(this.focusCellByIndex)
    }
  },

  methods: {
    /* init stuff */

    init () {
      const inputType = this.getRelevantInputType()
      for (let key = 0; key < this.length; key += 1) {
        this.setCellObject(key)
        this.setCellInputType(key, inputType)
        this.setCellWatcher(key)
      }
    },

    setCellObject (key) {
      this.$set(this.cells, key, { key, value: '' })
    },

    setCellInputType (index, inputType = SECURE_INPUT_TYPE) {
      this.$set(this.cellsInputTypes, index, inputType)
    },

    setCellWatcher (index) {
      const watchingProperty = `cells.${index}.value`

      this.watchers[watchingProperty] = this.$watch(
        watchingProperty,
        (newVal, oldVal) => this.onCellChanged(index, newVal, oldVal)
      )
    },

    /* handlers */

    onParentValueUpdated () {
      if (this.value.length !== this.length) {
        this.$emit('input', this.pinCodeComputed)
        return
      }

      this.value
        .split('')
        .forEach((cell, idx) => {
          this.cells[idx].value = cell || ''
        })
    },

    onCellChanged (index, newVal, oldVal) {
      if (!this.isTheCellValid(newVal, false)) {
        this.cells[index].value = ''
        return
      }

      this.focusNextCell()

      /* performing character preview if it's enabled */
      if (this.secure && this.characterPreview) {
        setTimeout(this.setCellInputType, this.charPreviewDuration, index)
      }
    },

    onCellErase (index, e) {
      const isThisCellFilled = this.cells[index].value.length

      if (!isThisCellFilled) {
        this.focusPreviousCell()
        e.preventDefault()
      }
    },

    onKeyDown (e) {
      switch (e.keyCode) {
        /* left arrow key */
        case 37:
          this.focusPreviousCell()
          break

        /* right arrow key */
        case 39:
          this.focusNextCell()
          break

        default:
          break
      }
    },

    /* reset stuff */

    reset () {
      this.unwatchCells()
      this.init()
      this.focusCellByIndex()
    },

    unwatchCells () {
      const watchers = Object.keys(this.watchers)
      watchers.forEach(name => this.watchers[name]())
    },

    /* helpers */

    isTheCellValid (cell, allowEmpty = true) {
      if (!cell) {
        return allowEmpty ? cell === '' : false
      }

      return !!cell.match(CELL_REGEXP)
    },

    getRelevantInputType () {
      return this.secure && !this.characterPreview
        ? SECURE_INPUT_TYPE
        : DEFAULT_INPUT_TYPE
    },

    focusPreviousCell () {
      if (!this.focusedCellIdx) return

      this.focusCellByIndex(this.focusedCellIdx - 1)
    },

    focusNextCell () {
      if (this.pinCodeComputed.length === this.length) {
        this.blurCellByIndex(this.focusedCellIdx)
        this.$emit('complete', this.pinCodeComputed)
        return
      }

      if (this.focusedCellIdx === this.length - 1) {
        this.focusCellByIndex(0)

        return
      }

      this.focusCellByIndex(this.focusedCellIdx + 1)
    },
    // The element of a cell, undefined when it is not rendered (any more): the autofocus is scheduled on the next tick
    // and the user may have left the page by then
    getCellElement (index) {
      return this.$refs[`${this.baseRefName}${index}`]?.[0]
    },
    blurCellByIndex (index = 0) {
      const el = this.getCellElement(index)

      if (!el) {
        return
      }

      el.blur()

      this.focusedCellIdx = null
    },
    focusCellByIndex (index = 0) {
      const el = this.getCellElement(index)

      if (!el) {
        return
      }

      el.focus()
      el.select()

      this.focusedCellIdx = index
    }
  }
})
</script>

<style lang="scss">
.vue-pincode-input-wrapper {
  display: inline-flex;
}

.vue-pincode-input {
  outline: none;
  margin: 3px;
  padding: 5px;
  max-width: 40px;
  text-align: center;
  font-size: 1.1rem;
  border: none;
  border-radius: 3px;
  box-shadow: 0 0 3px rgba(0, 0, 0, 0.5);
  caret-color: transparent;

  &::placeholder {
    color: #ccc;
  }
}

.vue-pincode-input:focus {
  box-shadow: 0 0 6px rgba(0, 0, 0, 0.5);
}
</style>
